// API service for backend integration
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_URL_API;

export interface VideoData {
  id?: number; // For backward compatibility
  _id?: string; // MongoDB ObjectId
  title: string;
  description: string;
  videoUrl: string;
  imageUrl?: string; // Thumbnail/ảnh đại diện cho video
  imageId?: string; // ID của ảnh thumbnail trong database
  imageFile?: File; // File ảnh để upload
  viewCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface FormulaData {
  id?: number; // For backward compatibility
  _id?: string; // MongoDB ObjectId
  title: string;
  description: string;
  type: 'article' | 'video';
  order: number | undefined;
  coinsRequired: number | undefined;
  content?: string;
  videoUrl?: string;
  imageUrl?: string; // Thumbnail/ảnh đại diện cho formula
  imageId?: string; // ID của ảnh thumbnail trong database
  imageFile?: File; // File ảnh để upload
  contentImageUrl?: string; // Ảnh content trong bài viết
  contentImageId?: string; // ID của ảnh content trong database
  contentImageFile?: File; // File ảnh content để upload
  createdAt: string;
  updatedAt: string;
}

export interface SocialLinksData {
  _id?: string; // MongoDB ObjectId
  facebook: string;
  telegram: string;
  createdAt: string;
  updatedAt: string;
}

export interface ImageData {
  _id: string; // MongoDB ObjectId
  originalName: string;
  filename: string;
  url: string;
  size: number;
  mimeType: string;
  uploadedBy: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export class ApiService {
  // Get authentication headers
  private static getAuthHeaders() {
    // Try localStorage first
    const token = localStorage.getItem('access_token');
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    }
    
    // Try Cookie (fallback)
    const Cookie = require('js-cookie');
    const cookieToken = Cookie.get('access_token');
    if (cookieToken) {
      return {
        'Authorization': `Bearer ${cookieToken}`,
        'Content-Type': 'application/json'
      };
    }
    
    throw new Error('Không tìm thấy JWT token. Vui lòng đăng nhập lại.');
  }

  // Handle API response
  private static handleResponse<T>(response: any): T {
    
    // Backend có thể trả về data trực tiếp hoặc trong response.data
    if (response.data) {
      // Nếu response.data có success field
      if (response.data.success !== undefined) {
        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'API Error');
        }
      }
      
      // Backend trả về {videos: Array, total, page, totalPages} hoặc {formulas: Array, total, page, totalPages}
      // Cần map thành {data: Array, total, page, totalPages} để khớp với TypeScript interface
      if (response.data.videos) {
        return {
          data: response.data.videos,
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          totalPages: response.data.totalPages
        } as T;
      }
      
      if (response.data.formulas) {
        return {
          data: response.data.formulas,
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          totalPages: response.data.totalPages
        } as T;
      }
      
      // Nếu response.data là data trực tiếp
      return response.data;
    }
    
    throw new Error('Invalid response format');
  }

  // Handle API error
  private static handleError(error: any, operation: string) {
    if (error.response) {
      // Server responded with error status
      
      if (error.response.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_info');
        throw new Error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
      }
      
      if (error.response.status === 400) {
        throw new Error(`Dữ liệu không hợp lệ: ${error.response.data?.message || 'Vui lòng kiểm tra lại thông tin'}`);
      }
      
      throw new Error(`${operation} failed: ${error.response.status} - ${error.response.statusText}`);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error(`${operation} failed: Không thể kết nối đến server`);
    } else {
      // Something else happened
      throw new Error(`${operation} failed: ${error.message}`);
    }
  }

  // ===== VIDEO MANAGEMENT =====

  // Get videos with pagination
  static async getVideos(page: number = 1, limit: number = 10): Promise<{
    data: VideoData[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/videos?page=${page}&limit=${limit}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error, 'Lấy danh sách video');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // Get all videos from backend API
  static async getAllVideos(): Promise<VideoData[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/videos`, {
        headers: this.getAuthHeaders()
      });
      
      // Handle different response formats from backend
      let videos: VideoData[] = [];
      
      if (Array.isArray(response.data)) {
        // Direct array response
        videos = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        // Wrapped in data object
        videos = response.data.data;
      } else if (response.data.videos && Array.isArray(response.data.videos)) {
        // Wrapped in videos object
        videos = response.data.videos;
      }
      
      return videos;
    } catch (error) {
      this.handleError(error, 'Lấy danh sách video từ backend');
      throw error;
    }
  }

  // Get video by ID
  static async getVideoById(id: string | number): Promise<VideoData | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/videos/${id}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse<VideoData>(response);
    } catch (error) {
      return null;
    }
  }

  // Create new video
  static async createVideo(videoData: Omit<VideoData, 'id' | '_id' | 'createdAt' | 'updatedAt'>): Promise<VideoData> {
    try {
      // Upload ảnh trước nếu có
      let imageUrl = videoData.imageUrl;
      if (videoData.imageFile) {
        try {
          const uploadResult = await this.uploadImage(videoData.imageFile);
          imageUrl = uploadResult.url;
        } catch (error) {
        }
      }
      
      const response = await axios.post(`${API_BASE_URL}/videos`, {
        title: videoData.title || '',
        description: videoData.description || '',
        videoUrl: videoData.videoUrl || '',
        imageUrl: imageUrl || ''
      }, {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      this.handleError(error, 'Tạo video mới');
      throw error;
    }
  }

  // Update video
  static async updateVideo(id: string | number, videoData: Partial<Omit<VideoData, 'id' | '_id' | 'createdAt'>>): Promise<VideoData | null> {
    try {
      // Upload ảnh trước nếu có
      let imageUrl = videoData.imageUrl;
      if (videoData.imageFile) {
        try {
          const uploadResult = await this.uploadImage(videoData.imageFile);
          imageUrl = uploadResult.url;
        } catch (error) {
        }
      }
      
      // Gửi data thực tế
      const updateData = {
        title: videoData.title || '',
        description: videoData.description || '',
        videoUrl: videoData.videoUrl || '',
        imageUrl: imageUrl || ''
      };
      
      const response = await axios.patch(`${API_BASE_URL}/videos/${id}`, updateData, {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      this.handleError(error, 'Cập nhật video');
      throw error;
    }
  }

  // Delete video
  static async deleteVideo(id: string | number): Promise<boolean> {
    try {
      
      await axios.delete(`${API_BASE_URL}/videos/${id}`, {
        headers: this.getAuthHeaders()
      });
      
      return true;
    } catch (error) {
      this.handleError(error, 'Xóa video');
      throw error;
    }
  }

  // Search videos
  static async searchVideos(query: string, page: number = 1, limit: number = 10): Promise<{
    data: VideoData[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/videos/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error, 'Tìm kiếm video');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // Increment video view count
  static async incrementVideoView(id: string | number): Promise<boolean> {
    try {
      
      await axios.post(`${API_BASE_URL}/videos/${id}/view`, {}, {
        headers: this.getAuthHeaders()
      });
      
      return true;
    } catch (error) {
      this.handleError(error, 'Tăng lượt xem video');
      throw error;
    }
  }

  // ===== FORMULA MANAGEMENT =====

  // Get formulas with pagination
  static async getFormulas(page: number = 1, limit: number = 10): Promise<{
    data: FormulaData[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/formulas?page=${page}&limit=${limit}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error, 'Lấy danh sách công thức');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // Get all formulas (backward compatibility)
  static async getAllFormulas(): Promise<FormulaData[]> {
    try {
      const result = await this.getFormulas(1, 1000); // Get all formulas
      return result.data; // TypeScript interface có field 'data'
    } catch (error) {
      this.handleError(error, 'Lấy tất cả công thức');
      throw error;
    }
  }

  // Get formulas by type
  static async getFormulasByType(type: 'article' | 'video', page: number = 1, limit: number = 10): Promise<{
    data: FormulaData[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/formulas/type/${type}?page=${page}&limit=${limit}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error, 'Lấy công thức theo loại');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // Get formula by ID
  static async getFormulaById(id: string | number): Promise<FormulaData | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/formulas/${id}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse<FormulaData>(response);
    } catch (error) {
      return null;
    }
  }

  // Create new formula
  static async createFormula(formulaData: Omit<FormulaData, 'id' | '_id' | 'createdAt' | 'updatedAt'>): Promise<FormulaData> {
    try {
      
      const response = await axios.post(`${API_BASE_URL}/formulas`, formulaData, {
        headers: this.getAuthHeaders()
      });
      
      return this.handleResponse<FormulaData>(response);
    } catch (error) {
      this.handleError(error, 'Tạo công thức mới');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // Update formula
  static async updateFormula(id: string | number, formulaData: Partial<Omit<FormulaData, 'id' | '_id' | 'createdAt'>>): Promise<FormulaData | null> {
    try {
      const response = await axios.patch(`${API_BASE_URL}/formulas/${id}`, formulaData, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse<FormulaData>(response);
    } catch (error) {
      this.handleError(error, 'Cập nhật công thức');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // Delete formula
  static async deleteFormula(id: string | number): Promise<boolean> {
    try {
      await axios.delete(`${API_BASE_URL}/formulas/${id}`, {
        headers: this.getAuthHeaders()
      });
      return true;
    } catch (error) {
      this.handleError(error, 'Xóa công thức');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // Search formulas
  static async searchFormulas(query: string, page: number = 1, limit: number = 10): Promise<{
    data: FormulaData[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/formulas/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error, 'Tìm kiếm công thức');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // Reorder formulas
  static async reorderFormulas(formulaIds: string[]): Promise<boolean> {
    try {
      await axios.post(`${API_BASE_URL}/formulas/reorder`, { formulaIds }, {
        headers: this.getAuthHeaders()
      });
      return true;
    } catch (error) {
      this.handleError(error, 'Sắp xếp lại công thức');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // ===== USER MANAGEMENT =====

  // Get current user
  static async getCurrentUser(): Promise<any> {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/current`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error, 'Lấy thông tin user hiện tại');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // Get user coins
  static async getUserCoins(): Promise<number> {
    try {
      const user = await this.getCurrentUser();
      return user.coins || 0;
    } catch (error) {
      return 0;
    }
  }

  // Deduct coins for action
  static async deductCoins(amount: number): Promise<boolean> {
    try {
      await axios.post(`${API_BASE_URL}/users/subtract-coins-for-action`, { amount }, {
        headers: this.getAuthHeaders()
      });
      return true;
    } catch (error) {
      this.handleError(error, 'Trừ xu cho hành động');
      throw error; // Re-throw to satisfy TypeScript
    }
  }

  // ===== SOCIAL LINKS MANAGEMENT =====

  // Get social links
  static async getSocialLinks(): Promise<SocialLinksData | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/social-links`, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse<SocialLinksData>(response);
    } catch (error) {
      // Nếu không tìm thấy social links, trả về null thay vì throw error
      if (error && typeof error === 'object' && 'response' in error && (error as any).response?.status === 404) {
        return null;
      }
      this.handleError(error, 'Lấy thông tin social links');
      throw error;
    }
  }

  // Create social links
  static async createSocialLinks(socialLinksData: Omit<SocialLinksData, '_id' | 'createdAt' | 'updatedAt'>): Promise<SocialLinksData> {
    try {
      const response = await axios.post(`${API_BASE_URL}/social-links`, socialLinksData, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse<SocialLinksData>(response);
    } catch (error) {
      this.handleError(error, 'Tạo social links mới');
      throw error;
    }
  }

  // Update social links
  static async updateSocialLinks(socialLinksData: Partial<Omit<SocialLinksData, '_id' | 'createdAt'>>): Promise<SocialLinksData> {
    try {
      const response = await axios.put(`${API_BASE_URL}/social-links`, socialLinksData, {
        headers: this.getAuthHeaders()
      });
      return this.handleResponse<SocialLinksData>(response);
    } catch (error) {
      this.handleError(error, 'Cập nhật social links');
      throw error;
    }
  }

  // ===== IMAGE UPLOAD =====
  
  // Upload image file to backend
  static async uploadImage(file: File): Promise<{ url: string; filename: string; imageId: string }> {
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API_BASE_URL}/images/upload`, formData, {
        headers: {
          'Authorization': this.getAuthHeaders().Authorization,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('✅ General image uploaded successfully:', response.data);
      console.log('🔍 General upload URL:', response.data.url);
      
      return {
        url: response.data.url,
        filename: response.data.filename,
        imageId: response.data.imageId
      };
    } catch (error) {
      this.handleError(error, 'Upload ảnh');
      throw error;
    }
  }

  // Upload image for video
  static async uploadVideoImage(videoId: string, file: File): Promise<{
    videoId: string;
    imageUrl: string;
    imageFileName: string;
    imageFileSize: number;
    imageId: string;
    updatedAt: string;
  }> {
    try {
      console.log('📤 Uploading image for video:', videoId, 'file:', file.name, 'size:', file.size);
      
      const formData = new FormData();
      formData.append('videoId', videoId);
      formData.append('image', file);
      
      console.log('📤 FormData contents:');
      // Convert FormData to array for logging
      const formDataArray = Array.from(formData.entries());
      formDataArray.forEach(([key, value]) => {
        console.log(key, value);
      });
      
      const response = await axios.post(`${API_BASE_URL}/videos/upload-image`, formData, {
        headers: {
          'Authorization': this.getAuthHeaders().Authorization,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('✅ Video image uploaded successfully:', response.data);
      console.log('🔍 Full response:', JSON.stringify(response.data, null, 2));
      console.log('🔍 Image URL from response:', response.data.data?.imageUrl);
      return response.data.data;
    } catch (error) {
      console.error('❌ Upload video image error:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        console.error('❌ Response status:', axiosError.response?.status);
        console.error('❌ Response data:', axiosError.response?.data);
      }
      this.handleError(error, 'Upload ảnh video');
      throw error;
    }
  }

  // Upload content image for formula
  static async uploadContentImage(formulaId: string, file: File): Promise<{
    formulaId: string;
    contentImageUrl: string;
    contentImageFileName: string;
    contentImageFileSize: number;
    contentImageId: string;
    updatedAt: string;
  }> {
    try {
      console.log('📤 Uploading content image for formula:', formulaId);
      
      const formData = new FormData();
      formData.append('formulaId', formulaId);
      formData.append('image', file);
      
      const response = await axios.post(`${API_BASE_URL}/formulas/upload-content-image`, formData, {
        headers: {
          'Authorization': this.getAuthHeaders().Authorization,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('✅ Content image uploaded successfully:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('❌ Upload content image error:', error);
      this.handleError(error, 'Upload ảnh content');
      throw error;
    }
  }

  // Get all images with pagination
  static async getImages(page: number = 1, limit: number = 20): Promise<{
    images: ImageData[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/images?page=${page}&limit=${limit}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      this.handleError(error, 'Lấy danh sách ảnh');
      throw error;
    }
  }

  // Get image by ID
  static async getImageById(id: string): Promise<ImageData | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/images/id/${id}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      return null;
    }
  }
}

// Export for backward compatibility
export const MockApiService = ApiService;
